import { Link, createFileRoute } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { formatCents } from '@pirouette/shared';
import { Badge, type BadgeProps } from '@/components/ui/Badge';
import { Empty } from '@/components/ui/Empty';
import {
  useArAging,
  useCapacityFill,
  useOwnerSnapshot,
  type ArAgingRow,
  type CapacityFillRow,
  type OwnerSnapshot,
  type OwnerSnapshotAction,
} from '@/data/bi';

// Owner BI dashboard. A "Do next" action queue (from bi_owner_snapshot.actions)
// sits up top, then KPI stat cards, an AR-aging table, and a capacity-fill
// table. Built on the same layout primitives as the owner dashboard / costume
// command center (StatCard / Section / SimpleTable) so it reads as one product.
//
// The /admin parent route already gates on the `admin` role server-side, so this
// child route inherits that guard (matches every other admin.* route).
export const Route = createFileRoute('/admin/bi/')({
  component: OwnerBiDashboard,
});

type BadgeTone = NonNullable<BadgeProps['tone']>;

// ---------------------------------------------------------------------------
// Formatting + fill-rate tone
// ---------------------------------------------------------------------------

// fill_rate may arrive as a 0–1 fraction or a 0–100 percentage depending on the
// RPC. Normalize to a 0–1 fraction so the badge tone + label are consistent.
function fillFraction(rate: number): number {
  if (!Number.isFinite(rate) || rate <= 0) return 0;
  return rate > 1 ? rate / 100 : rate;
}

function formatFill(rate: number): string {
  return `${Math.round(fillFraction(rate) * 100)}%`;
}

// Capacity health at a glance: danger under half full, warn under 80%, success
// otherwise.
function fillTone(rate: number): BadgeTone {
  const fraction = fillFraction(rate);
  if (fraction < 0.5) return 'danger';
  if (fraction < 0.8) return 'warn';
  return 'success';
}

// ---------------------------------------------------------------------------
// Layout primitives (mirror admin.dashboard.tsx / admin.costumes.index.tsx)
// ---------------------------------------------------------------------------

function StatCard({ label, value, hint }: { label: string; value: ReactNode; hint?: string }) {
  return (
    <article className="surface-elevated p-5">
      <p className="text-[var(--color-ink-muted)] text-[var(--text-sm)]">{label}</p>
      <p className="mt-2 font-[var(--font-display)] text-[var(--text-3xl)] tabular-nums">{value}</p>
      {hint && <p className="mt-1 text-[var(--color-ink-subtle)] text-[var(--text-xs)]">{hint}</p>}
    </article>
  );
}

function Section({
  id,
  title,
  description,
  action,
  children,
}: {
  id: string;
  title: string;
  description: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section aria-labelledby={`${id}-heading`} className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 id={`${id}-heading`} className="text-[var(--text-xl)]">
            {title}
          </h2>
          <p className="mt-1 max-w-3xl text-[var(--color-ink-muted)] text-[var(--text-sm)]">
            {description}
          </p>
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

function Num({ value }: { value: number }) {
  return <span className="font-medium tabular-nums">{value}</span>;
}

// ---------------------------------------------------------------------------
// "Do next" action queue
// ---------------------------------------------------------------------------

// Short, scannable suffix for an action: the count and/or the dollar amount the
// action is about. Either may be absent.
function actionMeta(action: OwnerSnapshotAction): string | null {
  const parts: string[] = [];
  if (action.count !== null) parts.push(String(action.count));
  if (action.amount_cents !== null) parts.push(formatCents(action.amount_cents));
  return parts.length > 0 ? parts.join(' · ') : null;
}

function ActionQueue({ actions }: { actions: OwnerSnapshotAction[] }) {
  return (
    <section aria-labelledby="do-next-heading" className="surface-elevated space-y-4 p-6">
      <div>
        <p className="tracking-[var(--tracking-wide)] text-[var(--color-ink-muted)] text-[var(--text-xs)] uppercase">
          Do next
        </p>
        <h2 id="do-next-heading" className="mt-1 text-[var(--text-xl)]">
          Action queue
        </h2>
      </div>
      <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {actions.map((action, index) => {
          const meta = actionMeta(action);
          return (
            <li key={`${action.kind}-${index}`}>
              <Link
                to={action.link}
                className="surface flex h-full items-center justify-between gap-3 p-4 transition-colors hover:bg-[var(--color-surface-sunken)]"
              >
                <span className="flex flex-col gap-1">
                  <span className="font-medium text-[var(--text-sm)]">{action.label}</span>
                  {meta && (
                    <span className="text-[var(--color-ink-muted)] text-[var(--text-xs)] tabular-nums">
                      {meta}
                    </span>
                  )}
                </span>
                <span aria-hidden className="text-[var(--color-ink-subtle)]">
                  →
                </span>
              </Link>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

// ---------------------------------------------------------------------------
// KPI grid
// ---------------------------------------------------------------------------

function KpiGrid({ snapshot }: { snapshot: OwnerSnapshot }) {
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      <StatCard
        label="Collected MTD"
        value={formatCents(snapshot.collected_mtd_cents)}
        hint="Payments received this month"
      />
      <StatCard
        label="Billed MTD"
        value={formatCents(snapshot.billed_mtd_cents)}
        hint="Charges raised this month"
      />
      <StatCard
        label="AR total"
        value={formatCents(snapshot.ar_total_cents)}
        hint="Outstanding receivable now"
      />
      <StatCard
        label="AR over 60 days"
        value={formatCents(snapshot.ar_over_60_cents)}
        hint="Aged 61+ days"
      />
      <StatCard
        label="Failing payments"
        value={snapshot.failing_payments}
        hint="Cards needing attention"
      />
      <StatCard
        label="Low-fill classes"
        value={snapshot.low_fill_classes}
        hint="Below target capacity"
      />
      <StatCard
        label="Unpaid costume $"
        value={formatCents(snapshot.unpaid_costume_cents)}
        hint="Costume balances owed"
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// AR aging table
// ---------------------------------------------------------------------------

function ArAgingTable({ rows }: { rows: ArAgingRow[] }) {
  return (
    <div className="surface overflow-x-auto">
      <table className="min-w-full text-left text-[var(--text-sm)]">
        <thead>
          <tr className="border-b border-[var(--color-border)]">
            <th className="px-4 py-3 font-medium">Family</th>
            <th className="px-4 py-3 text-right font-medium">Current</th>
            <th className="px-4 py-3 text-right font-medium">1–30</th>
            <th className="px-4 py-3 text-right font-medium">31–60</th>
            <th className="px-4 py-3 text-right font-medium">61–90</th>
            <th className="px-4 py-3 text-right font-medium">90+</th>
            <th className="px-4 py-3 text-right font-medium">Total</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.family_id} className="border-b border-[var(--color-border)] last:border-0">
              <td className="px-4 py-3 align-top font-medium">{row.family_name}</td>
              <td className="px-4 py-3 text-right align-top tabular-nums">
                {formatCents(row.current_cents)}
              </td>
              <td className="px-4 py-3 text-right align-top tabular-nums">
                {formatCents(row.d1_30)}
              </td>
              <td className="px-4 py-3 text-right align-top tabular-nums">
                {formatCents(row.d31_60)}
              </td>
              <td className="px-4 py-3 text-right align-top tabular-nums">
                {formatCents(row.d61_90)}
              </td>
              <td className="px-4 py-3 text-right align-top tabular-nums">
                {row.d90_plus > 0 ? (
                  <Badge tone="danger">{formatCents(row.d90_plus)}</Badge>
                ) : (
                  formatCents(row.d90_plus)
                )}
              </td>
              <td className="px-4 py-3 text-right align-top font-medium tabular-nums">
                {formatCents(row.total_cents)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Capacity fill table
// ---------------------------------------------------------------------------

function CapacityFillTable({ rows }: { rows: CapacityFillRow[] }) {
  return (
    <div className="surface overflow-x-auto">
      <table className="min-w-full text-left text-[var(--text-sm)]">
        <thead>
          <tr className="border-b border-[var(--color-border)]">
            <th className="px-4 py-3 font-medium">Class</th>
            <th className="px-4 py-3 text-right font-medium">Enrolled</th>
            <th className="px-4 py-3 text-right font-medium">Capacity</th>
            <th className="px-4 py-3 text-right font-medium">Waitlist</th>
            <th className="px-4 py-3 text-right font-medium">Fill rate</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.class_id} className="border-b border-[var(--color-border)] last:border-0">
              <td className="px-4 py-3 align-top font-medium">{row.class_name}</td>
              <td className="px-4 py-3 text-right align-top">
                <Num value={row.enrolled} />
              </td>
              <td className="px-4 py-3 text-right align-top">
                <Num value={row.capacity} />
              </td>
              <td className="px-4 py-3 text-right align-top">
                <Num value={row.waitlisted} />
              </td>
              <td className="px-4 py-3 text-right align-top">
                <Badge tone={fillTone(row.fill_rate)}>{formatFill(row.fill_rate)}</Badge>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Dashboard
// ---------------------------------------------------------------------------

function OwnerBiDashboard() {
  const snapshot = useOwnerSnapshot();
  const arAging = useArAging();
  const capacityFill = useCapacityFill();

  const snapshotData = snapshot.data;
  const actions = snapshotData?.actions ?? [];
  const arRows = arAging.data ?? [];
  const capacityRows = capacityFill.data ?? [];

  return (
    <div className="space-y-10">
      <header>
        <p className="tracking-[var(--tracking-wide)] text-[var(--color-ink-muted)] text-[var(--text-sm)] uppercase">
          Studio intelligence
        </p>
        <h1 className="mt-2">Owner BI</h1>
      </header>

      {/* Do next ---------------------------------------------------------- */}
      {snapshot.isLoading ? (
        <p aria-live="polite">Loading…</p>
      ) : snapshot.error ? (
        <Empty
          title="Snapshot unavailable"
          description="The owner snapshot could not be loaded for this session. Refresh to retry."
        />
      ) : actions.length > 0 ? (
        <ActionQueue actions={actions} />
      ) : (
        <Empty
          title="Nothing needs attention"
          description="No outstanding actions right now. New collections, capacity, and costume exceptions will surface here as they arise."
        />
      )}

      {/* KPIs ------------------------------------------------------------- */}
      <Section
        id="kpis"
        title="Key metrics"
        description="Month-to-date collections and billing, receivable totals and aging, plus open exception counts."
      >
        {snapshot.isLoading ? (
          <p aria-live="polite">Loading…</p>
        ) : snapshot.error || !snapshotData ? (
          <Empty
            title="Metrics unavailable"
            description="The owner snapshot could not be loaded for this session."
          />
        ) : (
          <KpiGrid snapshot={snapshotData} />
        )}
      </Section>

      {/* AR aging --------------------------------------------------------- */}
      <Section
        id="ar-aging"
        title="AR aging"
        description="Outstanding balance per family, bucketed by how overdue it is. Balances aged 90+ days are flagged."
      >
        {arAging.isLoading ? (
          <p aria-live="polite">Loading…</p>
        ) : arAging.error ? (
          <Empty
            title="AR aging unavailable"
            description="The receivable aging report could not be loaded for this session."
          />
        ) : arRows.length === 0 ? (
          <Empty
            title="No outstanding receivables"
            description="No family currently carries a balance. Aged balances will appear here as charges go unpaid."
          />
        ) : (
          <ArAgingTable rows={arRows} />
        )}
      </Section>

      {/* Capacity fill ---------------------------------------------------- */}
      <Section
        id="capacity-fill"
        title="Capacity fill"
        description="Enrollment against capacity per class, with a fill-rate badge: danger below 50%, warn below 80%, success otherwise."
      >
        {capacityFill.isLoading ? (
          <p aria-live="polite">Loading…</p>
        ) : capacityFill.error ? (
          <Empty
            title="Capacity unavailable"
            description="The capacity-fill report could not be loaded for this session."
          />
        ) : capacityRows.length === 0 ? (
          <Empty
            title="No classes to show"
            description="No classes with capacity were found. Classes will appear here once they exist with a set capacity."
          />
        ) : (
          <CapacityFillTable rows={capacityRows} />
        )}
      </Section>
    </div>
  );
}
